// Shared viewer for the live-preview debug log — the "Preview log" item in the
// 3-dot menu on every preview surface (transaction, visual, dashboard).
//
// Master-detail: the compact run summary on the left (newest first, with
// OK/WARN/ERROR badges); the selected run's full detail text on the right, with
// copy-to-clipboard. Reads from /api/livepreview/preview-log/*.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  fetchPreviewLogSummary, fetchPreviewLogDetail, type PreviewLogEntry,
} from '@/api/previewLog'

interface Props {
  open: boolean
  onClose: () => void
  projectName: string
  /** The object whose log to show (the previewed transaction/visual/dashboard). */
  objectName?: string
}

const STATUS_COLOR: Record<string, string> = {
  ERROR: '#ef4444',
  WARN: '#f59e0b',
  OK: '#22c55e',
}

function statusColor(s: string): string {
  return STATUS_COLOR[s] || 'var(--color-text-secondary)'
}

export default function PreviewLogModal({ open, onClose, projectName, objectName }: Props) {
  const [entries, setEntries] = useState<PreviewLogEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [detail, setDetail] = useState<string>('')
  const [detailLoading, setDetailLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const detailCache = useRef<Record<string, string>>({})

  const loadSummary = useCallback(() => {
    setEntries(null)
    setError(null)
    setSelected(null)
    setDetail('')
    detailCache.current = {}
    fetchPreviewLogSummary(projectName, objectName, 200)
      .then((r) => {
        const list = r.entries || []
        setEntries(list)
        // Auto-select the newest problem run, else the newest run.
        const first = list.find((e) => e.status === 'ERROR' || e.status === 'WARN') || list[0]
        if (first) setSelected(first.run_id)
      })
      .catch((e) => setError(e?.response?.data?.detail?.error?.message || e?.message || 'Failed to load preview log'))
  }, [projectName, objectName])

  // (Re)load whenever the modal opens or the object changes.
  useEffect(() => {
    if (open) loadSummary()
  }, [open, loadSummary])

  // ESC closes.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Load detail for the selected run (cached).
  useEffect(() => {
    if (!open || !selected) { setDetail(''); return }
    if (detailCache.current[selected] !== undefined) {
      setDetail(detailCache.current[selected])
      return
    }
    setDetailLoading(true)
    fetchPreviewLogDetail(projectName, selected, objectName)
      .then((r) => { detailCache.current[selected] = r.detail; setDetail(r.detail) })
      .catch((e) => {
        const msg = e?.response?.status === 404
          ? 'No detail for this run — it was a routine successful read (only mutations, deploys, and errors keep full detail).'
          : (e?.message || 'Failed to load run detail.')
        detailCache.current[selected] = ''
        setDetail('')
        setError(null)
        // Surface inline in the detail pane rather than as a modal-wide error.
        detailCache.current[selected] = `ℹ️ ${msg}`
        setDetail(`ℹ️ ${msg}`)
      })
      .finally(() => setDetailLoading(false))
  }, [open, selected, projectName, objectName])

  const copy = () => {
    navigator.clipboard?.writeText(detail).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }

  if (!open) return null

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: 20 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-bg, #fff)', color: 'var(--color-text)', borderRadius: 12, width: 'min(1000px, 96vw)', height: 'min(680px, 90vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.45)', overflow: 'hidden', border: '1px solid var(--color-border)' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid var(--color-border)' }}>
          <div>
            <strong style={{ fontSize: '1.0125rem' }}>Preview log</strong>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8625rem', marginLeft: 8 }}>
              {projectName}{objectName ? ` / ${objectName}` : ''}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={loadSummary}>Refresh</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>

        {/* Body: master-detail */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Summary list */}
          <div style={{ width: 360, borderRight: '1px solid var(--color-border)', overflowY: 'auto', flexShrink: 0 }}>
            {error && <div style={{ color: '#ef4444', fontSize: 14, padding: 14 }}>{error}</div>}
            {!error && entries === null && (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 14, padding: 14 }}>Loading…</div>
            )}
            {!error && entries !== null && entries.length === 0 && (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 14, padding: 14 }}>
                No preview activity logged yet. Run a preview, then refresh.
              </div>
            )}
            {entries?.map((e) => {
              const active = e.run_id === selected
              return (
                <button
                  key={e.run_id}
                  onClick={() => setSelected(e.run_id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                    padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--color-border)',
                    background: active ? 'var(--color-bg-secondary, #f1f5f9)' : 'transparent',
                    color: 'var(--color-text)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor(e.status), flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>{e.kind}{e.action && e.action !== e.kind ? ` · ${e.action}` : ''}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 13, color: statusColor(e.status), fontWeight: 600 }}>{e.status}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {e.outcome}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>{e.ts.replace('T', ' ')}</div>
                </button>
              )
            })}
          </div>

          {/* Detail pane */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {selected ? `Run ${selected}` : 'Select a run'}
              </span>
              {selected && detail && (
                <button className="btn btn-ghost btn-sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
              )}
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
              {detailLoading && <div style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>Loading detail…</div>}
              {!detailLoading && (
                <pre style={{ margin: 0, fontSize: 13, lineHeight: 1.5, fontFamily: 'var(--font-mono, ui-monospace, monospace)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {detail || (selected ? '' : 'Pick a run on the left to see what happened.')}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
